"use client";

/**
 * WhiteboardSessionRecap – self-contained end-of-session recap panel.
 *
 * The main app unmounts TranscriptionView (and the useWhiteboard hook) the
 * moment a session ends, so this component reads the finished session's
 * boards from the module-level session board store instead of from the hook.
 *
 * Mount once at the ListenView root (outside the session/ready ternary) and
 * pass `sessionActive` so it hides during live sessions and refreshes from
 * the store when the session ends.
 */
import { useAuth } from "@/components/AuthContext";
import { useFlashcards } from "@/hooks/useFlashcards";
import type { WhiteboardSessionBoard } from "@/lib/whiteboard/boardResults";
import {
  clearLastSessionBoards,
  getEngagedLastSessionBoards,
  subscribeLastSessionBoards,
} from "@/lib/whiteboard/sessionBoardStore";
import { motion } from "framer-motion";
import { X } from "lucide-react";
import * as React from "react";
import { createPortal } from "react-dom";
import { WhiteboardRecapGallery } from "./WhiteboardRecapGallery";

export interface WhiteboardSessionRecapProps {
  targetLanguage: string;
  instructionLanguage: string;
  languageLevel?: string;
  /** True while the live transcript / whiteboard hook is mounted. */
  sessionActive: boolean;
}

export function WhiteboardSessionRecap({
  targetLanguage,
  instructionLanguage,
  languageLevel,
  sessionActive,
}: WhiteboardSessionRecapProps) {
  const { user } = useAuth();
  const { isFlashcardSaved, saveFlashcard, fetchFlashcards } = useFlashcards(user?.id);

  const [boards, setBoards] = React.useState<WhiteboardSessionBoard[]>([]);
  const [dismissed, setDismissed] = React.useState(false);

  const refreshBoards = React.useCallback(() => {
    setBoards(getEngagedLastSessionBoards());
  }, []);

  React.useEffect(() => {
    if (sessionActive) {
      setBoards([]);
      return;
    }

    // Read immediately and subscribe while inactive. The session view and
    // useWhiteboard unmount during the same close transition; depending on
    // React/browser timing, its final store flush can happen after this effect.
    setDismissed(false);
    refreshBoards();
    const unsubscribe = subscribeLastSessionBoards(refreshBoards);
    const frame = requestAnimationFrame(refreshBoards);
    const settleTimer = window.setTimeout(refreshBoards, 80);
    return () => {
      unsubscribe();
      cancelAnimationFrame(frame);
      window.clearTimeout(settleTimer);
    };
  }, [refreshBoards, sessionActive]);

  React.useEffect(() => {
    if (boards.length > 0 && user?.id) {
      void fetchFlashcards();
    }
  }, [boards.length, user?.id, fetchFlashcards]);

  if (sessionActive || dismissed || boards.length === 0 || typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[90] flex justify-center px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-6 sm:pb-6">
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 320, damping: 30 }}
        className="pointer-events-auto relative w-full max-w-2xl"
      >
        <button
          type="button"
          onClick={() => {
            clearLastSessionBoards();
            setDismissed(true);
          }}
          className="absolute right-2 top-2 z-10 flex h-9 w-9 items-center justify-center rounded-md text-white/50 transition-colors hover:bg-white/[0.06] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/70"
          aria-label="Dismiss board recap"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="max-h-[min(60dvh,38rem)] overflow-y-auto rounded-lg shadow-2xl shadow-black/40">
          <WhiteboardRecapGallery
            boards={boards}
            targetLanguage={targetLanguage}
            instructionLanguage={instructionLanguage}
            languageLevel={languageLevel}
            userId={user?.id}
            isFlashcardSaved={isFlashcardSaved}
            saveFlashcard={saveFlashcard}
          />
        </div>
      </motion.div>
    </div>,
    document.body
  );
}
